import * as Haptics from 'expo-haptics';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedProps,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSequence,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import Svg, { Path } from 'react-native-svg';
import { Ionicons } from '@expo/vector-icons';

const AnimatedPath = Animated.createAnimatedComponent(Path);

type Props = {
  visible: boolean;
  onDone: () => void;
};

const SIG_PATH = 'M 10 40 C 40 5, 70 75, 110 38 C 145 8, 170 65, 210 35 C 225 22, 235 42, 240 38';
const SIG_LENGTH = 320;

function triggerHaptic() {
  void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy);
}

export function AgreementSealAnimation({ visible, onDone }: Props) {
  const { t } = useTranslation();
  const overlayOpacity = useSharedValue(0);
  const penX          = useSharedValue(-130);
  const sigDash       = useSharedValue(SIG_LENGTH);
  const sealScale     = useSharedValue(0);
  const sealRotate    = useSharedValue(-15);
  const sealOpacity   = useSharedValue(0);

  const sigAnimProps = useAnimatedProps(() => ({
    strokeDashoffset: sigDash.value,
  }));

  useEffect(() => {
    if (!visible) return;

    // Reset
    overlayOpacity.value = 0;
    penX.value     = -130;
    sigDash.value  = SIG_LENGTH;
    sealScale.value   = 0;
    sealRotate.value  = -15;
    sealOpacity.value = 0;

    const easeIO = Easing.inOut(Easing.quad);

    // Fade in overlay
    overlayOpacity.value = withTiming(1, { duration: 200 });

    // Phase 1: pen + signature (0–1000ms)
    penX.value    = withTiming(130, { duration: 1000, easing: easeIO });
    sigDash.value = withTiming(0,   { duration: 1000, easing: easeIO });

    // Phase 2: seal (1000ms+)
    sealOpacity.value = withDelay(1000, withTiming(1, { duration: 150 }));
    sealScale.value = withDelay(1000,
      withSequence(
        withSpring(1.2, { damping: 6, stiffness: 200 }),
        withSpring(1.0, { damping: 14, stiffness: 280 }),
      ),
    );
    sealRotate.value = withDelay(1000,
      withTiming(0, { duration: 500, easing: Easing.out(Easing.back(1.2)) }),
    );

    const hapticTimer = setTimeout(() => runOnJS(triggerHaptic)(), 1000);

    // Fade out at 2500ms
    const fadeTimer = setTimeout(() => {
      overlayOpacity.value = withTiming(0, { duration: 400 }, (finished) => {
        if (finished) runOnJS(onDone)();
      });
    }, 2500);

    return () => {
      clearTimeout(hapticTimer);
      clearTimeout(fadeTimer);
    };
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  const overlayStyle = useAnimatedStyle(() => ({ opacity: overlayOpacity.value }));
  const penStyle     = useAnimatedStyle(() => ({ transform: [{ translateX: penX.value }] }));
  const sealStyle    = useAnimatedStyle(() => ({
    opacity: sealOpacity.value,
    transform: [{ scale: sealScale.value }, { rotate: `${sealRotate.value}deg` }],
  }));

  if (!visible) return null;

  return (
    <Animated.View style={[styles.overlay, overlayStyle]} pointerEvents="none">
      {/* Phase 1 */}
      <View style={styles.signatureWrap}>
        <Svg width={250} height={80}>
          <AnimatedPath
            d={SIG_PATH}
            stroke="#C9A843"
            strokeWidth={2.5}
            strokeLinecap="round"
            fill="none"
            strokeDasharray={SIG_LENGTH}
            animatedProps={sigAnimProps}
          />
        </Svg>
        <Animated.View style={[styles.penWrap, penStyle]}>
          <Ionicons name="create" size={28} color="#C9A843" />
        </Animated.View>
      </View>

      {/* Phase 2 */}
      <Animated.View style={[styles.seal, sealStyle]}>
        <View style={styles.sealInner}>
          <Text style={styles.sealCheck}>✓</Text>
          <Text style={styles.sealText}>{t('common.valid')}</Text>
        </View>
      </Animated.View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(13,27,42,0.90)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999,
    gap: 40,
  },
  signatureWrap: {
    width: 250,
    height: 80,
    alignItems: 'center',
    justifyContent: 'center',
  },
  penWrap: {
    position: 'absolute',
    top: 18,
  },
  seal: {
    width: 140,
    height: 140,
    borderRadius: 70,
    borderWidth: 3,
    borderColor: '#C9A843',
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(201,168,67,0.08)',
  },
  sealInner: {
    alignItems: 'center',
    gap: 4,
  },
  sealCheck: {
    color: '#C9A843',
    fontSize: 42,
    fontWeight: '800',
    lineHeight: 50,
  },
  sealText: {
    color: '#C9A843',
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: 3,
  },
});
